import { useCallback, useMemo } from "react";
import { startOfMonth, startOfQuarter, startOfWeek } from "date-fns";
import { type Identifier, useGetIdentity } from "ra-core";
import { useSearchParams } from "react-router";

import { useIsAdmin } from "../sales/useIsAdmin";

export type DashboardPeriod = "week" | "month" | "quarter";

export type DashboardScope = {
  /** Account manager whose data is shown; null means "the whole team". */
  salesId: Identifier | null;
  /** True when salesId is null (team-wide view). Admin surfaces only. */
  isTeam: boolean;
  period: DashboardPeriod;
  /** Inclusive ISO start of the selected period. */
  periodStart: string;
  setSalesId: (id: Identifier | null) => void;
  setPeriod: (period: DashboardPeriod) => void;
  /** True while identity/admin status is still resolving. */
  isPending: boolean;
};

/** Query-string and storage value standing for the team-wide scope. */
const TEAM_OWNER = "team";

const STORAGE_KEY = "crm.dashboard.scope";

const DEFAULT_PERIOD: DashboardPeriod = "month";

const PERIODS: DashboardPeriod[] = ["week", "month", "quarter"];

type StoredScope = { owner?: string; period?: string };

/**
 * localStorage throws in some privacy contexts (Safari private browsing,
 * blocked third-party storage), and its content can be anything. Both sides are
 * guarded: the scope then simply does not survive a cold load.
 */
const readStoredScope = (): StoredScope => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredScope) : {};
  } catch {
    return {};
  }
};

const writeStoredScope = (scope: StoredScope) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // Ignored on purpose, see readStoredScope.
  }
};

const isPeriod = (value: string | null): value is DashboardPeriod =>
  value != null && PERIODS.includes(value as DashboardPeriod);

/** Sales ids are numeric in the database, but the URL only carries strings. */
const parseOwner = (value: string): Identifier =>
  /^\d+$/.test(value) ? Number(value) : value;

const startOfPeriod = (period: DashboardPeriod): string => {
  const now = new Date();
  if (period === "week") return startOfWeek(now).toISOString();
  if (period === "quarter") return startOfQuarter(now).toISOString();
  return startOfMonth(now).toISOString();
};

/**
 * The account manager and time window every dashboard widget reads from.
 *
 * With nothing chosen the scope is the whole team: the dashboard opens on
 * everything the reader is allowed to see, rather than on their own corner of
 * it. That default is admin-only — a non-admin is never widened past their own
 * accounts, whatever the URL, storage or default says.
 *
 * An explicit choice always beats the default: the state lives in the query
 * string so a view is linkable, and is mirrored to localStorage so the next
 * cold load reopens on the same scope. The URL wins over storage.
 *
 * Widgets build their filter as `isTeam ? {} : { sales_id: salesId }`, and must
 * hold off until `isPending` is false: until identity and admin status land,
 * the scope has not settled on its final value.
 */
export const useDashboardScope = (): DashboardScope => {
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  const { isAdmin, isPending: isAdminPending } = useIsAdmin();
  const [searchParams, setSearchParams] = useSearchParams();

  // Storage only seeds a cold load, so it is read once: later reads would fight
  // the query string, which is the live source of truth.
  const stored = useMemo(readStoredScope, []);
  const ownerParam = searchParams.get("owner") ?? stored.owner ?? null;
  const periodParam = searchParams.get("period") ?? stored.period ?? null;

  const ownId = identity?.id ?? null;
  const period = isPeriod(periodParam) ? periodParam : DEFAULT_PERIOD;

  const salesId = useMemo(() => {
    // Checked before the default, not just before the URL: a non-admin can
    // never be widened, and the team default would otherwise do exactly that.
    if (!isAdmin) return ownId;
    // Nothing chosen anywhere: the whole team.
    if (ownerParam == null || ownerParam === "") return null;
    return ownerParam === TEAM_OWNER ? null : parseOwner(ownerParam);
  }, [ownerParam, isAdmin, ownId]);

  const commit = useCallback(
    (owner: string, nextPeriod: DashboardPeriod) => {
      // Replace rather than push: toggling a control strip should not fill the
      // history with entries the back button has to walk through.
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("owner", owner);
          next.set("period", nextPeriod);
          return next;
        },
        { replace: true },
      );
      writeStoredScope({ owner, period: nextPeriod });
    },
    [setSearchParams],
  );

  const ownerValue = salesId == null ? TEAM_OWNER : String(salesId);

  const setSalesId = useCallback(
    (id: Identifier | null) => {
      // Only an admin may look beyond their own accounts.
      if (!isAdmin && id !== ownId) return;
      commit(id == null ? TEAM_OWNER : String(id), period);
    },
    [commit, isAdmin, ownId, period],
  );

  const setPeriod = useCallback(
    (next: DashboardPeriod) => commit(ownerValue, next),
    [commit, ownerValue],
  );

  return useMemo(
    () => ({
      salesId,
      isTeam: salesId == null,
      period,
      periodStart: startOfPeriod(period),
      setSalesId,
      setPeriod,
      isPending: isIdentityPending || isAdminPending,
    }),
    [salesId, period, setSalesId, setPeriod, isIdentityPending, isAdminPending],
  );
};
